import React from "react";
import { Scatter } from "react-chartjs-2";
import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    Tooltip,
    Legend
} from "chart.js";

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

// Custom plugin for quadrant lines & arrows
const quadrantPlugin = {
    id: "quadrants",
    beforeDraw(chart) {
        const { ctx, chartArea } = chart;
        const midX = (chartArea.left + chartArea.right) / 2;
        const midY = (chartArea.top + chartArea.bottom) / 2;

        ctx.save();
        ctx.strokeStyle = "#999";
        ctx.lineWidth = 1;

        // Vertical mid-line
        ctx.beginPath();
        ctx.moveTo(midX, chartArea.top);
        ctx.lineTo(midX, chartArea.bottom);
        ctx.stroke();

        // Horizontal mid-line
        ctx.beginPath();
        ctx.moveTo(chartArea.left, midY);
        ctx.lineTo(chartArea.right, midY);
        ctx.stroke();

        ctx.restore();
    }
};

const arrowPlugin = {
    id: "arrows",
    afterDraw(chart) {
        const { ctx, chartArea } = chart;

        ctx.save();

        // X axis → green
        ctx.fillStyle = "green";
        ctx.beginPath();
        ctx.moveTo(chartArea.right, chartArea.bottom);
        ctx.lineTo(chartArea.right - 10, chartArea.bottom - 5);
        ctx.lineTo(chartArea.right - 10, chartArea.bottom + 5);
        ctx.fill();

        // X axis ← red
        ctx.fillStyle = "red";
        ctx.beginPath();
        ctx.moveTo(chartArea.left, chartArea.bottom);
        ctx.lineTo(chartArea.left + 10, chartArea.bottom - 5);
        ctx.lineTo(chartArea.left + 10, chartArea.bottom + 5);
        ctx.fill();

        // Y axis ↑ green
        ctx.fillStyle = "green";
        ctx.beginPath();
        ctx.moveTo(chartArea.left, chartArea.top);
        ctx.lineTo(chartArea.left - 5, chartArea.top + 10);
        ctx.lineTo(chartArea.left + 5, chartArea.top + 10);
        ctx.fill();

        // Y axis ↓ red
        ctx.fillStyle = "red";
        ctx.beginPath();
        ctx.moveTo(chartArea.left, chartArea.bottom);
        ctx.lineTo(chartArea.left - 5, chartArea.bottom - 10);
        ctx.lineTo(chartArea.left + 5, chartArea.bottom - 10);
        ctx.fill();

        ctx.restore();
    }
};

// React Chart Component
export default function RiskScatterChart({ graphDataRows = [], mineOverallRow = null }) {
    const getPointColor = (label) => {
        return label === "Mine Overall" ? "rgba(238, 0, 0, 0.85)" : "rgba(78, 149, 217, 0.85)";
    };

    const parseValidNumber = (value) => {
        if (value === null || value === undefined) return null;
        const stringValue = String(value).trim();
        if (stringValue === "") return null;
        const numeric = Number(stringValue);
        if (Number.isNaN(numeric)) return null;
        return numeric;
    };

    const points = [];

    (graphDataRows || []).forEach((row) => {
        const x = parseValidNumber(row.inherent);
        const y = parseValidNumber(row.efficacy);
        if (x === null || y === null) return;
        points.push({
            label: row.risk,
            x,
            y,
            isMineOverall: row.risk === "Mine Overall",
        });
    });

    if (mineOverallRow) {
        const x = parseValidNumber(mineOverallRow.inherent);
        const y = parseValidNumber(mineOverallRow.efficacy);
        if (x !== null && y !== null) {
            points.push({
                label: "Mine Overall",
                x,
                y,
                isMineOverall: true,
            });
        }
    }

    const data = {
        datasets: points.map((point) => ({
            label: point.label,
            data: [{ x: point.x, y: point.y }],
            pointRadius: 10,
            backgroundColor: getPointColor(point.isMineOverall ? "Mine Overall" : point.label),
        })),
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: "top",
                labels: {
                    color: "black",
                    font: {
                        size: 12,
                    },
                },
            },
        },
        scales: {
            x: {
                min: 0,
                max: 10,
                border: {
                    color: "black"
                },
                grid: {
                    display: false
                },
                ticks: {
                    color: "black",
                    font: {
                        size: 14
                    }
                },
                title: {
                    display: true,
                    color: "black",
                    font: {
                        size: 14,
                        weight: "bold"
                    },
                    text: "Inherent Risk"
                }
            },
            y: {
                min: 0,
                max: 10,
                border: {
                    color: "black"
                },
                grid: {
                    display: false
                },
                ticks: {
                    color: "black",
                    font: {
                        size: 14
                    }
                },
                title: {
                    display: true,
                    color: "black",
                    font: {
                        size: 14,
                        weight: "bold"
                    },
                    text: "Control Efficacy"
                }
            }
        }
    };

    return (
        <div style={{ width: "100%", height: "400px", backgroundColor: "#ffffff", padding: "16px", borderRadius: "4px" }}>
            <Scatter
                data={data}
                options={options}
                plugins={[quadrantPlugin, arrowPlugin]}
            />
        </div>
    );
}
